import React from 'react'
import { clsx } from 'clsx'
import { Button, Heading, Input, Select, Text } from '../../components/ui'
import type { RelatorioPeriodo } from '../../types/comercialContatosPortal'
import { BrokerCombobox } from '../Qualidade/BrokerCombobox'

export const contatosPortalFiltroFieldClass = 'w-full'

type ContatosPortalFiltrosSectionProps = {
  children: React.ReactNode
  onBuscar: () => void
  onLimpar: () => void
  loading?: boolean
}

export function FiltroField({
  label,
  className,
  children,
}: {
  label: string
  className?: string
  children: React.ReactNode
}) {
  return (
    <div className={className ?? 'min-w-0'}>
      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
        {label}
      </Text>
      {children}
    </div>
  )
}

/** Filtros no padrão Comercial (Cotações / Gestão): card + grid uniforme + ações à direita. */
export function ContatosPortalFiltrosSection({
  children,
  onBuscar,
  onLimpar,
  loading = false,
}: ContatosPortalFiltrosSectionProps) {
  return (
    <div className="px-6 py-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
        <div className="mb-4">
          <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white">
            Filtros
          </Heading>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">{children}</div>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="solid" size="sm" onClick={onBuscar} disabled={loading}>
            {loading ? 'Buscando...' : 'Buscar'}
          </Button>
          <Button variant="outline" size="sm" onClick={onLimpar} disabled={loading}>
            Limpar filtros
          </Button>
        </div>
      </div>
    </div>
  )
}

type ContatosPortalCorretorFiltroInputProps = {
  value: string | null
  initialBroker?: { name: string; id_scgp: number | null } | null
  onChange: (
    id: string | null,
    broker: { id: string; name: string; id_scgp: number | null } | null
  ) => void
  disabled?: boolean
}

/** Combobox de corretor — mesmo padrão do Cockpit (busca por SCGP ou nome). */
export const ContatosPortalCorretorFiltroInput: React.FC<ContatosPortalCorretorFiltroInputProps> = ({
  value,
  initialBroker,
  onChange,
  disabled = false,
}) => {
  return (
    <BrokerCombobox
      value={value}
      initialBroker={initialBroker}
      onChange={onChange}
      disabled={disabled}
      className={contatosPortalFiltroFieldClass}
      placeholder="Digite o código SCGP ou nome do corretor (mín. 4 caracteres)"
    />
  )
}

type ComercialUsuario = { id: string; name: string }

type ContatosPortalResponsavelFiltroInputProps = {
  value: string
  onChange: (userId: string) => void
  usuarios: ComercialUsuario[]
  disabled?: boolean
  /** Quando true (sem Ver valores), oculta placeholder — padrão Cotacoes. */
  restricted?: boolean
}

/** Select de Comercial responsável — mesmo padrão de /comercial/cotacoes. */
export const ContatosPortalResponsavelFiltroInput: React.FC<
  ContatosPortalResponsavelFiltroInputProps
> = ({ value, onChange, usuarios, disabled = false, restricted = false }) => {
  return (
    <Select
      value={value}
      onChange={e => onChange(e.target.value)}
      disabled={disabled}
      className={contatosPortalFiltroFieldClass}
    >
      {!restricted && <option value="">Todos</option>}
      {usuarios.map(user => (
        <option key={user.id} value={user.id}>
          {user.name}
        </option>
      ))}
    </Select>
  )
}

type ContatosPortalPeriodoFiltroInputProps = {
  value: RelatorioPeriodo
  onChange: (value: RelatorioPeriodo) => void
  invalid?: boolean
  layout?: 'inline' | 'grid'
}

export const ContatosPortalPeriodoFiltroInput: React.FC<ContatosPortalPeriodoFiltroInputProps> = ({
  value,
  onChange,
  invalid = false,
  layout = 'inline',
}) => {
  const fieldClass = clsx(
    contatosPortalFiltroFieldClass,
    invalid && '!ring-2 !ring-red-500 dark:!ring-red-500'
  )

  if (layout === 'grid') {
    return (
      <>
        <FiltroField label="Data Inicial">
          <Input
            type="date"
            value={value.dataInicio}
            onChange={e => onChange({ ...value, dataInicio: e.target.value })}
            required
            aria-label="Data inicial do período"
            className={fieldClass}
          />
        </FiltroField>
        <FiltroField label="Data Final">
          <Input
            type="date"
            value={value.dataFim}
            onChange={e => onChange({ ...value, dataFim: e.target.value })}
            min={value.dataInicio || undefined}
            required
            aria-label="Data final do período"
            className={fieldClass}
          />
        </FiltroField>
      </>
    )
  }

  return (
    <div className="flex min-w-0 w-full items-center gap-2" title="Período (obrigatório)">
      <Input
        type="date"
        value={value.dataInicio}
        onChange={e => onChange({ ...value, dataInicio: e.target.value })}
        required
        aria-label="Data inicial do período"
        className={clsx(fieldClass, 'flex-1')}
      />
      <span className="shrink-0 select-none text-sm text-gray-500 dark:text-gray-400" aria-hidden>
        até
      </span>
      <Input
        type="date"
        value={value.dataFim}
        onChange={e => onChange({ ...value, dataFim: e.target.value })}
        min={value.dataInicio || undefined}
        required
        aria-label="Data final do período"
        className={clsx(fieldClass, 'flex-1')}
      />
    </div>
  )
}
